import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Parser from 'html-react-parser';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';

import {
  clearFiles,
  createFileVersion,
  fetchFiles,
  updateFilesOnFilesList,
  updateFilesOnSelectedFilesList,
} from '../../actions/Files';
import {
  runFileAction,
  setMiniActiveFile,
  setMiniOpenedFiles,
  setMiniOverlay,
  setUploadProgress,
  showMiniConfirmationModal,
  showModalMini,
  showModalTopMini,
} from '../../actions/Mini';

import { APIcreateDownload } from '../../api/Downloads';

import {
  getActiveDocumentInfo,
  sendAdobePostMessage,
} from '../../helpers/Adobe';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { findAction } from '../../helpers/Mini';
import { fileObject } from '../../helpers/Uploader';
import { eventDispatch } from '../../helpers/Events';

import ProcessingSpinner from '../../components/ProcessingSpinner';

const AdobeSave = ({
  miniConfig,
  runAction,
  handleCreateVersion,
  refreshFiles,
  openModal,
  openModalTop,
  updateFileOnList,
  handleUploadProgress,
  uploadProgress,
  handleSetMiniOpenedFiles,
  filesTotal,
  handleConfirmationModal,
  handleActiveFile,
  collection,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeDocStatus, setActiveDocStatus] = useState(null);

  const saveNewVersion = async () => {
    setLoading(true);
    handleUploadProgress(0);

    const action = findAction(miniConfig.actions, 'save-new-version');
    const activeDocument = await getActiveDocumentInfo();
    runAction({ ...activeDocument }, action);
  };

  const exportJPG = async () => {
    setLoading(true);
    handleUploadProgress(0);

    const action = findAction(miniConfig.actions, 'export-as-jpg');
    runAction({ extension: 'jpg' }, action);
    openModal(
      'MiniFileUploadModal',
      {
        className: 'modal-upload',
        files: [fileObject],
        beforeClose: () => {
          setLoading(false);
          refreshFiles();
        },
      },
    );
  };

  const exportPNG = async () => {
    setLoading(true);
    handleUploadProgress(0);

    const action = findAction(miniConfig.actions, 'export-as-png');
    runAction({ extension: 'png' }, action);
    openModal(
      'MiniFileUploadModal',
      {
        className: 'modal-upload',
        files: [fileObject],
        beforeClose: () => {
          setLoading(false);
          refreshFiles();
        },
      },
    );
  };

  const exportTIFF = async () => {
    setLoading(true);
    handleUploadProgress(0);

    const action = findAction(miniConfig.actions, 'export-as-tiff');
    runAction({ extension: 'tiff' }, action);
    openModal(
      'MiniFileUploadModal',
      {
        className: 'modal-upload',
        files: [fileObject],
        beforeClose: () => {
          setLoading(false);
          refreshFiles();
        },
      },
    );
  };

  const saveAsNewPsd = async () => {
    setLoading(true);
    handleUploadProgress(0);

    const action = findAction(miniConfig.actions, 'save-as-new-psd');
    runAction({ extension: 'psd' }, action);
    openModal(
      'MiniFileUploadModal',
      {
        className: 'modal-upload',
        files: [fileObject],
        beforeClose: () => {
          setLoading(false);
          refreshFiles();
        },
      },
    );
  };

  const createPackage = async () => {
    setLoading(true);
    handleUploadProgress(0);

    sendAdobePostMessage('create-package');
  };

  const handleMessage = useCallback(async (event) => {
    const { name } = event.data;
    const { fileParams } = event.data;
    const { fileUuid } = event.data;

    if (miniConfig.integrationId === 'adobe-photoshop') {
      if (name === 'save-new-version-complete') {
        const params = {
          uuid: fileUuid,
          path: fileParams.path,
          size: fileParams.size,
          copy_comments: null,
        };

        handleCreateVersion(fileUuid, params)
          .then((response) => {
            updateFileOnList(response);
            addFlashMessage({
              type: 'success',
              text: 'Saved new version',
            });
            sendAdobePostMessage('handle-opened-documents');
          })
          .catch((error) => {
            if (error.response.status === 404) {
              addFlashMessage({
                type: 'danger',
                text: 'File not found. Try saving it as a new file.',
              });
              refreshFiles();
              sendAdobePostMessage('file-not-found', { uuid: JSON.parse(error.config.data).uuid });
            } else {
              addFlashMessage({
                type: 'danger',
                text: Parser(`<strong>Something went wrong.</strong><br />${error}`),
              });
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }

      if (name === 'update-upload-progress') {
        const { progress } = event.data;

        handleUploadProgress(parseInt(progress));
      }

      if (name === 'set-active-document-status') {
        const { type, uuid } = event.data;

        setActiveDocStatus(type);
        handleActiveFile(uuid);
      }

      if (name === 'set-opened-files') {
        const { openedFiles } = event.data;

        handleSetMiniOpenedFiles(openedFiles);
      }
    }

    if (miniConfig.integrationId === 'adobe-indesign') {
      if (name === 'save-get-data') {
        const { files } = event.data;
        const action = findAction(miniConfig.actions, `save-package`);
        runAction(null, action, null, files);
      }

      if (name === 'open-upload-modal') {
        const { files, requireCollection, bulkVersioning } = event.data;

        const filesToUpload = files.map(f => ({ ...fileObject, ...f }));

        openModal(
          'MiniFileUploadModal',
          {
            className: 'modal-upload',
            files: filesToUpload,
            beforeClose: () => {
              setLoading(false);
              refreshFiles();
            },
            requireCollection,
            bulkVersioning,
          },
        );
      }

      if (name === 'open-upload-collection-select') {
        const { files, requireCollection, bulkVersioning } = event.data;

        openModal(
          'MiniFileUploadModal',
          {
            className: 'modal-upload',
            files,
            beforeClose: () => {
              setLoading(false);
              refreshFiles();
            },
            bulkVersioning,
            requireCollection,
            allowCollectionSelect: false,
          },
        );

        openModalTop(
          'CollectionSelectModal',
          {
            handleCollectionSelect: (selectedCollection) => {
              eventDispatch('collectionSelected', selectedCollection);
            },
            enableAddNew: true,
            showSmartSection: false,
          },
        );
      }

      if (name === 'open-package') {
        if (!collection) return;

        APIcreateDownload({
          files: [],
          collections: [collection.uuid],
          file_sizes: [{ id: 'original' }],
          file_formats: ['original'],
          request_uuid: null,
          versions: [],
        }).then(response => {
          const openFileName = event.data.data.data.original_name;

          const openData = {
            url: response.data.url,
            file: openFileName,
            downloadFolder: event.data.data.downloadFolder,
          };

          sendAdobePostMessage('open-package-finish', openData);
        });
      }

      if (name === 'error-active-document-not-saved') {
        const modalParams = {
          title: 'Save current document first',
          text: 'Please save the current document before performing this action',
          onConfirm: () => (
            new Promise((resolve) => { resolve(); })
          ),
        };
        handleConfirmationModal(modalParams);
      }
    }

    if (name === 'error-occured' || name === 'error-opening-package') {
      setLoading(false);
      handleUploadProgress(0);

      const { error } = event.data;
      if (window.Rollbar) {
        window.Rollbar.error(error);
      }

      addFlashMessage({
        type: 'danger',
        text: `An error occured. Please try again later.`,
      });
    }
  }, [
    handleCreateVersion,
    handleSetMiniOpenedFiles,
    handleUploadProgress,
    refreshFiles,
    updateFileOnList,
    handleActiveFile,
    miniConfig.integrationId,
    collection,
    handleConfirmationModal,
    miniConfig.actions,
    openModal,
    runAction,
    openModalTop,
  ]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    sendAdobePostMessage('handle-opened-documents');
  }, [filesTotal]);

  useEffect(() => {
    sendAdobePostMessage('handle-init');
  }, []);

  if (!(miniConfig.integrationId.startsWith('adobe'))) return null;

  if (miniConfig.integrationId === 'adobe-photoshop') {
    return (
      <div className="adobe-save-container">
        {!loading && (
          <>
            <div className="integration-save-logo-wrapper" id="StockpressLogoAdobeIntegration">
              <img
                src="/images/stockpress-brand-mark.png"
                alt="Stockpress"
                className={classnames('integration-save-logo', { 'integration-save-logo-greyscale': activeDocStatus !== 'saved' })}
              />
            </div>
            <UncontrolledTooltip fade={false} placement="bottom" target="StockpressLogoAdobeIntegration">
              {(!activeDocStatus || activeDocStatus === 'no-active-document') && 'No document opened'}
              {activeDocStatus === 'saved' && 'Current document stored in Stockpress'}
              {activeDocStatus === 'unsaved' && 'Current document not stored in Stockpress'}
            </UncontrolledTooltip>
          </>
        )}
        {(activeDocStatus === 'saved' || activeDocStatus === 'unsaved') && (
          <>
            <UncontrolledDropdown
              group
              disabled={loading}
              size="sm"
              className="integration-save-dropdown"
            >
              <Button
                id="AdobeSaveDropdown"
                color="secondary"
                onClick={() => {
                  if (activeDocStatus === 'saved') {
                    saveNewVersion();
                  } else {
                    saveAsNewPsd();
                  }
                }}
                disabled={loading}
                size="sm"
              >
                <ProcessingSpinner show={loading} size="small" />
                <span className="integration-save-dropdown-icon">
                  {!loading && (
                    <i className="far fa-save" />
                  )}
                </span>
                {loading && (
                  <span>
                    ({uploadProgress}%)
                  </span>
                )}
                {!loading && (
                  <span className="integration-save-dropdown-label">
                    {activeDocStatus === 'saved' ? 'Save version' : 'Save as new PSD'}
                  </span>
                )}
              </Button>
              <DropdownToggle
                caret
                color="secondary"
              />
              <DropdownMenu>
                {activeDocStatus === 'saved' && (
                <DropdownItem onClick={() => {
                  saveNewVersion();
                }}
                >
                  Save version
                </DropdownItem>
                )}
                <DropdownItem onClick={() => {
                  saveAsNewPsd();
                }}
                >
                  Save as new PSD
                </DropdownItem>
                <DropdownItem onClick={() => {
                  exportJPG();
                }}
                >
                  Export as JPG
                </DropdownItem>
                <DropdownItem onClick={() => {
                  exportPNG();
                }}
                >
                  Export as PNG
                </DropdownItem>
                <DropdownItem onClick={() => {
                  exportTIFF();
                }}
                >
                  Export as TIFF
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            {!loading && (
              <UncontrolledTooltip fade={false} placement="bottom" target="AdobeSaveDropdown">
                {activeDocStatus === 'saved' && 'Save version'}
                {activeDocStatus === 'unsaved' && 'Save as new PSD'}
              </UncontrolledTooltip>
            )}
          </>
        )}
      </div>
    );
  }

  if (miniConfig.integrationId === 'adobe-indesign') {
    return (
      <div className="adobe-save-container">
        <div className="integration-save-logo-wrapper" id="StockpressLogoAdobeIntegration">
          <img
            src="/images/stockpress-brand-mark.png"
            alt="Stockpress"
            className="integration-save-logo"
          />
        </div>
        <UncontrolledDropdown
          group
          disabled={loading}
          size="sm"
          className="integration-save-dropdown"
        >
          <Button
            id="AdobeSaveDropdown"
            color="secondary"
            onClick={createPackage}
            disabled={loading}
            size="sm"
          >
            <ProcessingSpinner show={loading} size="small" />
            <span className="integration-save-dropdown-icon">
              {!loading && (
              <i className="far fa-save" />
              )}
            </span>
            {!loading && (
            <span className="integration-save-dropdown-label">
              Package...
            </span>
            )}
          </Button>
          {/* <DropdownToggle
            caret
            color="secondary"
          />
          <DropdownMenu>
            <DropdownItem onClick={() => {
              createPackage();
            }}
            >
              Package...
            </DropdownItem>
          </DropdownMenu> */}
        </UncontrolledDropdown>
      </div>
    );
  }

  return null;
};

const mapStateToProps = state => ({
  collection: state.Collections.collection,
  sidebarOpen: state.Mini.sidebarOpen,
  miniConfig: state.Mini.config,
  uploadProgress: state.Mini.uploadProgress,
  filesTotal: state.Files.filesTotal,
});

const mapDispatchToProps = dispatch => ({
  runAction: (file, action, event = null, files = []) => {
    dispatch(runFileAction(file, action, event, files));
  },
  handleCreateVersion: (fileUuid, params) => (
    dispatch(createFileVersion(fileUuid, params))
  ),
  refreshFiles: () => {
    dispatch(clearFiles());
    dispatch(fetchFiles());
  },
  openModal: (modalName, params) => {
    dispatch(showModalMini(modalName, params));
  },
  openModalTop: (modalName, params) => {
    dispatch(showModalTopMini(modalName, params));
  },
  updateFileOnList: (file) => {
    dispatch(updateFilesOnFilesList([file]));
    dispatch(updateFilesOnSelectedFilesList([file]));
  },
  handleUploadProgress: (uploadProgress) => {
    dispatch(setUploadProgress(uploadProgress));
  },
  handleSetMiniOpenedFiles: (openedFiles) => {
    dispatch(setMiniOpenedFiles(openedFiles));
  },
  handleActiveFile: (fileUuid) => {
    dispatch(setMiniActiveFile(fileUuid));
  },
  handleConfirmationModal: (modalParams) => {
    dispatch(showMiniConfirmationModal(modalParams));
  },
  handleMiniOverlay: (overlay) => (
    dispatch(setMiniOverlay(overlay))
  ),
});

AdobeSave.propTypes = {
  miniConfig: PropTypes.object.isRequired,
  runAction: PropTypes.func.isRequired,
  handleCreateVersion: PropTypes.func.isRequired,
  refreshFiles: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  openModalTop: PropTypes.func.isRequired,
  updateFileOnList: PropTypes.func.isRequired,
  handleUploadProgress: PropTypes.func.isRequired,
  uploadProgress: PropTypes.number.isRequired,
  handleSetMiniOpenedFiles: PropTypes.func.isRequired,
  handleConfirmationModal: PropTypes.func.isRequired,
  filesTotal: PropTypes.number.isRequired,
  handleActiveFile: PropTypes.func.isRequired,
  collection: PropTypes.object,
};

AdobeSave.defaultProps = {
  collection: null,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AdobeSave));
